import { QueryKey, useQuery } from '@tanstack/react-query';

import { GenreDto } from '@cineamo/api/models/movie/GenreDto.types';
import { MovieDto as MovieV1Dto } from '@cineamo/api/models/movie/v1/MovieDto.types';
import { MovieDto as MovieV2Dto } from '@cineamo/api/models/movie/v2/MovieDto.types';

type MovieDto = MovieV1Dto | MovieV2Dto;

import { TMDB_IMAGE_RESOLUTION } from '@/constants/tmdb-constants';

import {
    getMovieBackdropBlurDataImage,
    getMovieBackdropImage,
    getMovieLogoImage,
    getMovieOverview,
    getMoviePosterBlurDataImage,
    getMoviePosterImage,
    getMovieReleaseDate,
    getMovieReleaseYear,
    getMovieRuntime,
    getMovieTitle,
    getMovieTrailer,
    VideoDtoEnhanced
} from '@cineamo/api/helpers/movie-helper';

import { getMovieAgeRating } from '@/helpers/movie-helper';
import {
    getLanguageCodeFromString,
    getCountryCodeFromString,
    ISO_3166_1_LOCALE
} from '@cineamo/ui/src/helpers/locale-helper';

type CachedMovieDataReturn = {
    data: CachedMovieData;
};

export type CachedMovieData = {
    id: MovieDto['id'];
    tmdbId: MovieDto['tmdbId'];
    title: string;
    releaseDate: string;
    releaseYear: string;
    ageRating: string;
    runtime: number;
    overview: string;
    genres: GenreDto[];
    trailer: VideoDtoEnhanced;
    backdropImages: Record<TMDB_IMAGE_RESOLUTION, string>;
    backdropImageURL_ORIGINAL: string;
    backdropImageURL_W780: string;
    backdropBlurDataImage: string;
    posterImages: Record<TMDB_IMAGE_RESOLUTION, string>;
    posterImageURL_W780: string;
    posterBlurDataImage: string;
    logoImageURL_W780: string;
    movie: MovieDto;
};

export function useCachedMovieData<TKey extends QueryKey = QueryKey>(
    options: {
        movie: MovieDto;
        countryCode: ISO_3166_1_LOCALE;
        locale: string;
    },
    deps: TKey = undefined
): CachedMovieDataReturn {
    const { data } = useQuery({
        queryKey: [
            'cached-movie-data',
            `movieTmdbId-${options.movie?.tmdbId}`,
            `locale-${options.locale}`,
            ...(deps ?? []).map((dep) => JSON.stringify(dep))
        ],
        queryFn: () => getCachedMovieData(options)
    });
    return { data };
}

export function getCachedMovieData(options: {
    movie: MovieDto;
    countryCode: ISO_3166_1_LOCALE;
    locale: string;
}): CachedMovieData {
    const ageRating = getMovieAgeRating(options.movie, options.countryCode);
    const language = getLanguageCodeFromString(options.locale);
    const countryCode = options.countryCode || getCountryCodeFromString(options.locale);

    return options?.movie
        ? {
              id: options.movie.id,
              tmdbId: options.movie.tmdbId,
              title: getMovieTitle(options.movie, options.locale, options.countryCode),
              releaseDate: getMovieReleaseDate(options.movie, options.countryCode),
              releaseYear: getMovieReleaseYear(options.movie, options.countryCode),
              ageRating: ageRating,
              runtime: getMovieRuntime(options.movie, options.locale),
              overview: getMovieOverview(options.movie, options.locale),
              genres: options.movie.genres,
              trailer: getMovieTrailer(options.movie, {
                  language,
                  countryCode
              }),
              backdropImages: Object.values(TMDB_IMAGE_RESOLUTION).reduce<Record<TMDB_IMAGE_RESOLUTION, string>>(
                  (previousValue, resolution) => {
                      previousValue[resolution] = getMovieBackdropImage(options.movie, {
                          language,
                          countryCode,
                          usePosterAsFallback: true,
                          usePlaceholder: false,
                          imageOptions: {
                              resolution
                          }
                      });
                      return previousValue;
                  },
                  {} as Record<TMDB_IMAGE_RESOLUTION, string>
              ),
              backdropImageURL_ORIGINAL: getMovieBackdropImage(options.movie, {
                  language,
                  countryCode,
                  usePosterAsFallback: true,
                  usePlaceholder: false,
                  imageOptions: {
                      resolution: TMDB_IMAGE_RESOLUTION.ORIGINAL
                  }
              }),
              backdropImageURL_W780: getMovieBackdropImage(options.movie, {
                  language,
                  countryCode,
                  usePosterAsFallback: true,
                  usePlaceholder: false,
                  imageOptions: {
                      resolution: TMDB_IMAGE_RESOLUTION.W780
                  }
              }),
              backdropBlurDataImage: getMovieBackdropBlurDataImage(options.movie, {
                  language,
                  countryCode,
                  usePosterAsFallback: false,
                  usePlaceholder: false
              }),
              posterImages: Object.values(TMDB_IMAGE_RESOLUTION).reduce<Record<TMDB_IMAGE_RESOLUTION, string>>(
                  (previousValue, resolution) => {
                      previousValue[resolution] = getMoviePosterImage(options.movie, {
                          language,
                          countryCode,
                          useBackdropAsFallback: true,
                          usePlaceholder: false,
                          imageOptions: {
                              resolution
                          }
                      });
                      return previousValue;
                  },
                  {} as Record<TMDB_IMAGE_RESOLUTION, string>
              ),
              posterImageURL_W780: getMoviePosterImage(options.movie, {
                  language,
                  countryCode,
                  useBackdropAsFallback: true,
                  usePlaceholder: false,
                  imageOptions: {
                      resolution: TMDB_IMAGE_RESOLUTION.W780
                  }
              }),
              posterBlurDataImage: getMoviePosterBlurDataImage(options.movie, {
                  language,
                  countryCode,
                  useBackdropAsFallback: false,
                  usePlaceholder: false
              }),
              // Movie logo (title treatment) in the viewer's language only — same picking logic as
              // the web hero; no fallback, so a missing logo falls back to the title text downstream.
              logoImageURL_W780: getMovieLogoImage(options.movie, {
                  language,
                  countryCode,
                  usePlaceholder: false,
                  imageOptions: {
                      resolution: TMDB_IMAGE_RESOLUTION.W780
                  }
              }),
              movie: options.movie
          }
        : null;
}
